import { useEffect, useState } from 'react';
import { getCharacters, removePk, deleteCharacter, type Character } from '@/shared/services/characterService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Swords, Shield, Skull, Trash2, Loader2, AlertTriangle, X } from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { CLASSES_PT, NATION_COLORS } from '@/features/Admin/labels';

// Memo `nation_colors`: Capella #ca69d3 (rosa/lilas), Procyon #2a7ddd (azul).
const NATION_NAMES: Record<number, { name: string; icon: typeof Swords }> = {
  1: { name: 'Capella', icon: Swords },
  2: { name: 'Procyon', icon: Shield },
};

export default function CharactersPage() {
  const { refreshCash } = useAuth();
  const { notify } = useToast();
  const confirm = useConfirm();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Modal de delete com confirmacao por digitacao do nome
  const [deleteTarget, setDeleteTarget] = useState<Character | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');

  const fetchChars = () => {
    getCharacters()
      .then(setCharacters)
      .catch(() => setCharacters([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchChars(); }, []);

  const handleRemovePk = async (charIdx: number) => {
    const ok = await confirm({
      title: 'Limpar penalidade PK?',
      description: 'A penalidade atual deste personagem sera removida. Essa acao nao pode ser desfeita.',
      tone: 'warning',
      icon: <Skull size={24} />,
      confirmLabel: 'Limpar PK',
    });
    if (!ok) return;

    setActionLoading(charIdx);
    try {
      // API limpa PK de TODOS os chars offline da conta de uma vez (nao usa charIdx).
      // Decisao de seguranca: charIdx nao vai mais pro frontend.
      const res = await removePk();
      notify('success', res.message || 'PK removido!');
    } catch (err: any) {
      notify('error', err?.message || 'Erro ao remover PK');
    } finally {
      setActionLoading(null);
    }
  };

  const openDeleteModal = (char: Character) => {
    setDeleteTarget(char);
    setDeleteTyped('');
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteTyped('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTyped !== deleteTarget.name) {
      notify('error', 'Nome digitado nao confere');
      return;
    }
    const charIdx = deleteTarget.characterIdx;
    setActionLoading(charIdx);
    closeDeleteModal();
    try {
      const res = await deleteCharacter(charIdx);
      notify('success', res.message || 'Personagem deletado');
      fetchChars();
      refreshCash();
    } catch (err: any) {
      notify('error', err?.message || 'Erro ao deletar');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin text-[#8fb9de]" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Gerenciar Personagens</h1>

      {characters.length === 0 ? (
        <p className="text-sm text-white/65 text-center py-12">Nenhum personagem encontrado</p>
      ) : (
        <div className="space-y-4">
          {characters.map(char => {
            const classId = char["class"] ?? char.style;
            const cls = CLASSES_PT[classId] || `Classe ${classId}`;
            const nation = NATION_NAMES[char.nation];
            const nationColor = NATION_COLORS[char.nation];
            const NationIcon = nation?.icon || Swords;
            const busy = actionLoading === char.characterIdx;

            return (
              <div key={char.characterIdx} className="surface-card rounded-md p-0 overflow-hidden">
                <div className="flex items-center">
                  {/* Class Icon */}
                  <div className="w-14 h-14 m-4 shrink-0 rounded-md panel-well flex items-center justify-center overflow-hidden">
                    <img
                      src={`/assets/class/i_${classId}.png`}
                      alt={cls}
                      className="w-9 h-9 object-contain"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 py-3 pr-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-base font-bold text-white">{char.name}</h3>
                        <span className="text-white/65 text-xs">Lv. {char.lev}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/60">
                        {nation ? (
                          <span className="flex items-center gap-1">
                            <NationIcon size={12} style={{ color: nationColor }} />
                            <span style={{ color: nationColor }}>{nation.name}</span>
                          </span>
                        ) : (
                          <span className="text-white/65">Sem nação</span>
                        )}
                        <span>{cls}</span>
                        <span>Alz: {char.alz?.toLocaleString('pt-BR') ?? 0}</span>
                        <span>STR: {char.str} DEX: {char.dex} INT: {char.int} PNT: {char.pnt}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <button
                        disabled={busy}
                        onClick={() => handleRemovePk(char.characterIdx)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium
                                   bg-white/5 text-white/60 border border-white/10
                                   hover:bg-white/10 transition-all disabled:opacity-50"
                      >
                        <Skull size={14} />
                        Limpar PK
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => openDeleteModal(char)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium
                                   bg-red-500/15 text-red-500 border border-red-500/30
                                   hover:bg-red-500/25 transition-all disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        Deletar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de delete com confirmacao por nome */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeDeleteModal(); }}
        >
          <div
            className="surface-card rounded-md max-w-md w-full p-6 border border-red-500/30 shadow-[0_0_60px_rgba(239,68,68,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 shrink-0 rounded-md bg-red-500/15 border border-red-500/30 flex items-center justify-center text-red-400">
                <AlertTriangle size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white">Deletar personagem</h2>
                <p className="text-xs text-white/60 mt-0.5">
                  Esta ação é <span className="text-red-400 font-semibold">permanente</span> e
                  registrada no sistema.
                </p>
              </div>
              <button
                onClick={closeDeleteModal}
                className="text-white/40 hover:text-white transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="bg-[#0d141b] border border-white/10 rounded-md p-3 mb-4">
              <p className="text-[10px] text-white/45 uppercase tracking-wider mb-1">
                Personagem a deletar
              </p>
              <p className="text-base font-bold text-white">
                {deleteTarget.name}{' '}
                <span className="text-white/55 font-normal">— Lv. {deleteTarget.lev}</span>
              </p>
            </div>

            <label className="block text-xs text-white/65 mb-1.5">
              Para confirmar, digite o nome do personagem:
            </label>
            <input
              type="text"
              autoFocus
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder={deleteTarget.name}
              className="w-full bg-[#0d141b] border border-white/15 rounded-md px-3 py-2 text-sm
                         text-white placeholder:text-white/30 font-mono
                         focus:outline-none focus:border-red-500/50"
            />

            <div className="flex gap-2 mt-5">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold
                           bg-white/5 text-white/75 border border-white/10
                           hover:bg-white/10 transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteTyped !== deleteTarget.name}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold transition cursor-pointer
                           bg-red-500 text-white border border-red-500
                           hover:bg-red-400 disabled:bg-red-500/30 disabled:border-red-500/30
                           disabled:cursor-not-allowed disabled:text-white/50"
              >
                Deletar permanentemente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
